import React, { useContext, useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, MapContext, ZoomableGroup } from 'react-simple-maps';
import { BUCKET_COLORS, BUCKET_ORDER } from '../route-intelligence/lib/driveBucket';
import PanelTakeaway from '../shared/PanelTakeaway';
import InfoTip from '../shared/InfoTip';
import { EmptyState } from '../shared/StateMessage';
import { fmtCount } from '../shared/format';
import { BTN, CARD_PAD, CARD_TITLE, chipClass } from './liStyles';

// Around the park — Route Intelligence's map pattern (react-simple-maps, a zoomable
// group, the SafeMarker guard, the same county-line base map from us-atlas), zoomed to
// the park. Pins take the first four colours of Route's drive ramp — closer is greener,
// as on Route — filled when open now, hollow when not. Band chips filter the pins; a
// second tap on a band opens its full list. The map is a local view, never the face of
// the page, and it draws no routes: the place detail links out to Google Maps for that.
const COUNTIES_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json';
const W = 800;
const H = 460;
const DEG = (2 * Math.PI) / 360;

// Route's guard: a pin whose coordinates do not project is skipped, so one bad row
// cannot break the map.
function SafeMarker({ coordinates, ...rest }) {
  const ctx = useContext(MapContext);
  const projection = ctx && ctx.projection;
  if (!projection || !Array.isArray(coordinates)) return null;
  const projected = projection(coordinates);
  if (!projected || !Number.isFinite(projected[0]) || !Number.isFinite(projected[1])) return null;
  return <Marker coordinates={coordinates} {...rest} />;
}

const bandColor = (i) => BUCKET_COLORS[BUCKET_ORDER[Math.min(i, 3)]] || BUCKET_COLORS.unknown;

const AroundMap = ({ panel, onOpen, onOpenDrill, showHeader = false, answerPins }) => {
  const facts = panel?.facts || {};
  const park = facts.park;
  const pins = useMemo(() => (Array.isArray(facts.pins) ? facts.pins : []), [facts.pins]);
  const bands = useMemo(() => (Array.isArray(facts.bands) ? facts.bands : []), [facts.bands]);
  // Item 3 — the active need's answer venues, overlaid on top of the base
  // local_map pins as a distinct "result" layer. Each fn_li_answer entry carries
  // lat/lng (confirmed in the fixture), so this is a pure frontend overlay — no
  // backend change needed. Filtered to finite coordinates so one bad row cannot
  // break the map (same guard the base pins rely on SafeMarker for). Not filtered
  // by the drive-band chips: those scope the base context pins only; the answer
  // layer shows the current query's full result set regardless of band.
  const answerLayer = useMemo(
    () => (Array.isArray(answerPins) ? answerPins.filter((a) => Number.isFinite(a.lat) && Number.isFinite(a.lng)) : []),
    [answerPins],
  );
  const [activeBand, setActiveBand] = useState(null);
  const [zoom, setZoom] = useState(1);

  const bandIndex = (minutes) => {
    const i = bands.findIndex((b) => minutes != null && minutes <= b.max_minutes);
    return i === -1 ? bands.length - 1 : i;
  };

  // Fit every pin around the park: the scale that makes the farthest pin sit inside
  // the frame, in both directions, with the park at the centre.
  const scale = useMemo(() => {
    if (!park || pins.length === 0) return 20000;
    let dLng = 0.02;
    let dLat = 0.02;
    pins.forEach((p) => {
      if (Number.isFinite(p.lng)) dLng = Math.max(dLng, Math.abs(p.lng - park.lng));
      if (Number.isFinite(p.lat)) dLat = Math.max(dLat, Math.abs(p.lat - park.lat));
    });
    const byLng = (W * 0.46) / (dLng * DEG);
    const byLat = ((H * 0.46) * Math.cos(park.lat * DEG)) / (dLat * DEG);
    return Math.min(byLng, byLat);
  }, [park, pins]);

  const pickBand = (band) => {
    if (activeBand === band.max_minutes) onOpenDrill(band.drill);
    else setActiveBand(band.max_minutes);
  };

  const shownPins = pins.filter((p) => activeBand == null || (p.drive_minutes != null && p.drive_minutes <= activeBand));
  const r = 4 / Math.max(zoom, 1);

  return (
    <section id="panel-local_map" className={`${CARD_PAD} flex-1 flex flex-col`} aria-label={panel?.title || 'Around the park'}>
      {showHeader && (
        <>
          <h2 className={`${CARD_TITLE} mb-2`}>
            {panel?.title || 'Around the park'}
            <InfoTip text="Every mapped place around the park, coloured by drive time. Filled pins are open now. Tap a pin to open it; tap a drive-time chip to filter, and tap it again for the full list." />
          </h2>
          <PanelTakeaway sentence={panel?.sentence} />
        </>
      )}

      {!park || pins.length === 0 ? (
        <EmptyState message="No places are mapped yet — they appear as the area is scanned." />
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap mb-3" role="group" aria-label="Drive time">
            <button type="button" className={chipClass(activeBand == null)} onClick={() => setActiveBand(null)}>All</button>
            {bands.map((b, i) => (
              <button key={b.max_minutes} type="button" aria-pressed={activeBand === b.max_minutes} className={`${chipClass(activeBand === b.max_minutes)} inline-flex items-center gap-1.5`} onClick={() => pickBand(b)}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: bandColor(i) }} aria-hidden="true" />
                {b.label} · {fmtCount(b.places)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-h-0 rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900">
            <ComposableMap
              width={W}
              height={H}
              projection="geoMercator"
              projectionConfig={{ center: [park.lng, park.lat], scale }}
              className="w-full h-full"
            >
              <ZoomableGroup center={[park.lng, park.lat]} zoom={zoom} minZoom={1} maxZoom={8} onMoveEnd={({ zoom: z }) => setZoom(z)}>
                <Geographies geography={COUNTIES_URL}>
                  {({ geographies }) => geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="rgba(229, 231, 235, 0.4)"
                      stroke="#fff"
                      className="dark:fill-gray-800 dark:stroke-gray-900"
                      strokeWidth={0.5 / Math.max(zoom / 2, 1)}
                      style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                    />
                  ))}
                </Geographies>
                {shownPins.map((p) => {
                  const color = bandColor(bandIndex(p.drive_minutes));
                  return (
                    <SafeMarker key={p.entity_id} coordinates={[p.lng, p.lat]} onClick={() => onOpen(p)} style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' }, pressed: {} }}>
                      <circle r={r} fill={p.open_now ? color : 'transparent'} stroke={color} strokeWidth={1.2 / Math.max(zoom, 1)} />
                      <title>{`${p.name}${p.category_label ? ` · ${p.category_label}` : ''}${p.drive_minutes != null ? ` · ${p.drive_minutes} min` : ''}`}</title>
                    </SafeMarker>
                  );
                })}
                {/* Item 3 — the active need's answer venues, rendered AFTER the base
                    pins so they sit on top as a distinct "result" layer. They reuse
                    the SAME drive-band colour ramp as the base pins (bandColor of
                    bandIndex(drive_minutes)) and the SAME filled=open / hollow=closed
                    semantics, so the legend and the coloured drive-time chips still
                    read correctly. What marks them as query results is size + a
                    white halo: a larger white circle sits behind a larger coloured
                    pin, so the answer pin pops over the smaller base pins without
                    introducing a new colour that would fight the legend. Not filtered
                    by the drive-band chips (those scope the base context pins only);
                    the answer layer shows the current query's full result set
                    regardless of band. Clicking opens the place detail via
                    onOpen(answer) — answer objects carry `drill`, so onOpen pushes
                    the fn_li_place_detail drill, the same path as clicking an answer
                    card. */}
                {answerLayer.map((a) => {
                  const color = bandColor(bandIndex(a.drive_minutes));
                  return (
                    <SafeMarker key={`ans-${a.entity_id}`} coordinates={[a.lng, a.lat]} onClick={() => onOpen(a)} style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' }, pressed: {} }}>
                      <circle r={r * 1.7} fill="#fff" />
                      <circle r={r * 1.3} fill={a.open_now ? color : 'transparent'} stroke={color} strokeWidth={1.6 / Math.max(zoom, 1)} />
                      <title>{`${a.name}${a.category_label ? ` · ${a.category_label}` : ''}${a.drive_minutes != null ? ` · ${a.drive_minutes} min` : ''}`}</title>
                    </SafeMarker>
                  );
                })}
                <SafeMarker coordinates={[park.lng, park.lat]}>
                  <circle r={8 / Math.max(zoom, 1)} fill="#32B1FF" stroke="#fff" strokeWidth={2 / Math.max(zoom, 1)} />
                  <title>{park.name}</title>
                </SafeMarker>
              </ZoomableGroup>
            </ComposableMap>
            <div className="absolute top-2 right-2 flex flex-col gap-1">
              <button type="button" aria-label="Zoom in" className={`${BTN} !px-2 !py-0.5`} onClick={() => setZoom((z) => Math.min(z * 1.6, 8))}>+</button>
              <button type="button" aria-label="Zoom out" className={`${BTN} !px-2 !py-0.5`} onClick={() => setZoom((z) => Math.max(z / 1.6, 1))}>−</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Filled pins are open now · hollow pins are closed now
              {facts.shown_label ? ` · ${facts.shown_label}` : ''}
            </span>
          </div>
        </>
      )}
    </section>
  );
};

export default AroundMap;
